import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/avi6rx8r_WhatsApp%20Image%202026-02-28%20at%2014.45.44%20%283%29.jpeg",
    alt: "Exterior",
    category: "exterior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/ho3mic30_WhatsApp%20Image%202026-02-28%20at%2014.15.30.jpeg",
    alt: "Outdoor Dining",
    category: "exterior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/7hts6rhl_WhatsApp%20Image%202026-02-28%20at%2014.45.44.jpeg",
    alt: "Living Room",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/idfs9g92_WhatsApp%20Image%202026-02-28%20at%2014.45.44%20%282%29.jpeg",
    alt: "Lounge Area",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/61mtbmux_WhatsApp%20Image%202026-02-28%20at%2014.15.31.jpeg",
    alt: "Dining Area",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/g7m2peu1_WhatsApp%20Image%202026-02-28%20at%2014.15.30%20%282%29.jpeg",
    alt: "Kitchen",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/r2xlr5u6_WhatsApp%20Image%202026-02-28%20at%2014.15.30%20%283%29.jpeg",
    alt: "Kitchen Island",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/zv8fohiz_WhatsApp%20Image%202026-02-28%20at%2014.45.46.jpeg",
    alt: "Kitchen Detail",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/fy8rgjs0_WhatsApp%20Image%202026-02-28%20at%2014.15.29%20%281%29.jpeg",
    alt: "Main Bedroom",
    category: "bedroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/dlpsv85t_WhatsApp%20Image%202026-02-28%20at%2014.15.29.jpeg",
    alt: "Bedroom Details",
    category: "bedroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/bzark7pk_WhatsApp%20Image%202026-02-28%20at%2014.15.30%20%281%29.jpeg",
    alt: "Bedroom View",
    category: "bedroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/flb7xsh2_WhatsApp%20Image%202026-02-28%20at%2014.15.31%20%281%29.jpeg",
    alt: "Treehouse Suite",
    category: "bedroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/lg6ur0og_WhatsApp%20Image%202026-02-28%20at%2014.45.46%20%281%29.jpeg",
    alt: "Bed with Towels",
    category: "bedroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/bbnpakxe_WhatsApp%20Image%202026-02-28%20at%2014.45.46%20%282%29.jpeg",
    alt: "Towel Detail",
    category: "bedroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/btsnxyfo_WhatsApp%20Image%202026-02-28%20at%2014.45.45%20%281%29.jpeg",
    alt: "Bathroom",
    category: "bathroom"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/ahwcni5t_WhatsApp%20Image%202026-02-28%20at%2014.45.45%20%282%29.jpeg",
    alt: "Hallway",
    category: "interior"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/knm9z570_WhatsApp%20Image%202026-02-28%20at%2014.45.44%20%281%29.jpeg",
    alt: "Outdoor Deck",
    category: "outdoor"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/flew933y_WhatsApp%20Image%202026-02-28%20at%2014.45.45.jpeg",
    alt: "BBQ Area",
    category: "outdoor"
  },
];

const categories = [
  { id: "all", label: "All" },
  { id: "exterior", label: "Exterior" },
  { id: "interior", label: "Interior" },
  { id: "bedroom", label: "Bedrooms" },
  { id: "bathroom", label: "Bathroom" },
  { id: "outdoor", label: "Outdoor" },
];

const HeroSection = () => (
  <section className="pt-12 pb-8 bg-brand-cream" data-testid="gallery-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <p className="section-subheading">Visual Journey</p>
        <h1 className="section-heading">Gallery</h1>
        <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
          Explore the beauty of Weaver's Nest Cottage through our gallery. 
          Every corner tells a story of tranquility and natural elegance.
        </p>
      </motion.div>
    </div>
  </section>
);

const Lightbox = ({ image, onClose, onPrev, onNext, currentIndex, totalImages }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>
      
      {/* Navigation */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 text-white/70 hover:text-white p-2 z-10"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-10 h-10" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 text-white/70 hover:text-white p-2 z-10"
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-10 h-10" />
      </button>
      
      {/* Image */}
      <motion.img
        key={image.src}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        src={image.src}
        alt={image.alt}
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      {/* Counter */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70">
        {currentIndex + 1} / {totalImages}
      </div>
    </motion.div>
  </AnimatePresence>
);

const GalleryGrid = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const filteredImages = activeCategory === "all" 
    ? IMAGES 
    : IMAGES.filter(img => img.category === activeCategory);
  
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxImage(filteredImages[index]);
  };
  
  const closeLightbox = () => {
    setLightboxImage(null);
  };
  
  const goToPrev = () => {
    const newIndex = lightboxIndex === 0 ? filteredImages.length - 1 : lightboxIndex - 1;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredImages[newIndex]);
  };
  
  const goToNext = () => {
    const newIndex = lightboxIndex === filteredImages.length - 1 ? 0 : lightboxIndex + 1;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredImages[newIndex]);
  };

  return (
    <section className="py-12 md:py-20 bg-white" data-testid="gallery-grid">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? "bg-brand-sage text-white"
                  : "bg-brand-cream text-brand-charcoal hover:bg-brand-muted"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        
        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {filteredImages.map((image, index) => {
            // Create varied sizes for bento effect
            const isLarge = index === 0 || index === 3;
            
            return (
              <motion.div
                key={image.src}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`gallery-item cursor-pointer ${
                  isLarge ? "md:col-span-2 md:row-span-2" : ""
                }`}
                onClick={() => openLightbox(index)}
                data-testid={`gallery-item-${index}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            );
          })}
        </div>
      </div>
      
      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox
          image={lightboxImage}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
          currentIndex={lightboxIndex}
          totalImages={filteredImages.length}
        />
      )}
    </section>
  );
};

export default function GalleryPage() {
  return (
    <div data-testid="gallery-page">
      <HeroSection />
      <GalleryGrid />
    </div>
  );
}
